import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCheckboxModule, MatCheckboxChange } from '@angular/material/checkbox';
import { take } from 'rxjs/internal/operators/take';
import { ManagementClientControllerService, ManagementClientResponse } from 'api-client';
import { ManagementCategoryClippingControllerService, ManagementCategoryResponse, ManagementTagClippingControllerService, ManagementTagResponse } from 'api-clipping';
import { SocialApiService } from '../../../../shared/services/social-api.service';
import { SharedService } from '../../../../shared/services/shared.service';
import { SocialClientSummary, SocialExcludedProfile, SocialKeyword, SocialKeywordHealth, SocialSet, SocialSetSummary } from '../../../../shared/interfaces/social.interfaces';

interface CategoryWithTags extends ManagementCategoryResponse {
	tags?: ManagementTagResponse[];
	opened?: boolean;
}

type SectionKey = 'keywords' | 'tags' | 'rules' | 'examples' | 'excluded';

// Admin > Settings > "Социјални мрежи · Досиеја" (docs/SOCIJALNI-FAZA1-EKRAN.md section 2): a
// redesign of the per-client screen from SOCIJALNI-FAZA1-KLIENTI.md after the manager found the
// previous version confusing on staging. The word "сет" never appears on screen - internally the
// model is unchanged (a client still has one or more SocialKeywordSet rows), but a client with
// exactly one of them shows its keywords/tags/rules/examples directly, top to bottom, with no
// extra click to "open a set" - only a client with a deliberate second "група" (e.g.
// "Конкуренција") sees named, collapsible cards. Left = only clients that already have
// keywords (a fresh client is reached through "+ Нов клиент" instead, which searches the full
// client list and opens an empty screen for whichever one is picked).
@Component({
	selector: 'app-social-dossiers',
	standalone: true,
	imports: [CommonModule, FormsModule, MatButtonModule, MatInputModule, MatFormFieldModule, MatIconModule, MatProgressSpinnerModule, MatExpansionModule, MatCheckboxModule],
	templateUrl: './social-dossiers.component.html',
	styleUrl: './social-dossiers.component.scss',
})
export class SocialDossiersComponent implements OnInit {
	clientSearchText = '';
	clients: SocialClientSummary[] = [];
	clientsLoading = false;
	unassignedCount = 0;

	showNewClientPicker = false;
	newClientQuery = '';
	newClientResults: ManagementClientResponse[] = [];
	newClientSearching = false;

	selectedClientId: string | null = null;
	selectedClientName = '';
	selectedIsUnassigned = false;
	deletingClient = false;

	sets: SocialSetSummary[] = [];
	setsLoading = false;
	// Only used in the "Без клиент" bucket - a flat list of orphan групи, never the single-client
	// flattened view (which uses addingGroup/newGroupName below instead).
	newSetName = '';

	// "+ Додај уштe една група" (section 2) - only offered once a client already has one group.
	addingGroup = false;
	newGroupName = '';

	expandedSetId: string | null = null;
	expandedSet: SocialSet | null = null;
	expandedSetLoading = false;

	openSections: Record<SectionKey, boolean> = { keywords: true, tags: true, rules: true, examples: true, excluded: true };
	savedFlash: Record<SectionKey, boolean> = { keywords: false, tags: false, rules: false, examples: false, excluded: false };

	newKeywordText = '';
	keywordHealth: Record<string, SocialKeywordHealth> = {};

	// CLIPPING-610 point 5: inline rename - one keyword/profile editable at a time.
	editingKeywordId: string | null = null;
	editingKeywordText = '';
	editingExcludedId: string | null = null;
	editingExcludedLabel = '';

	// Krug 2 point 1: rename a group/set (new or existing) the same way - ✎ left of ✕.
	editingSetId: string | null = null;
	editingSetName = '';

	// "+ друг таг" po zbor (section 2) - one keyword's extra-tags picker open at a time.
	keywordTagPickerKeyword: SocialKeyword | null = null;
	keywordTagSelectedIds: string[] = [];

	categories: CategoryWithTags[] = [];
	categoriesLoaded = false;
	selectedTagIds: string[] = [];

	rulesText = '';
	savingRules = false;

	newExampleSnippet = '';
	newExampleDecision = true;
	newExampleReason = '';

	excludedProfiles: SocialExcludedProfile[] = [];
	newExcludedLink = '';
	newExcludedLabel = '';

	assignClientQuery = '';
	assignClientResults: ManagementClientResponse[] = [];
	assignClientSearching = false;

	// "Премести кај клиент" (docs/SOCIJALNI-FAZA1-EKRAN.md section 2) - a quick-access version of
	// the assign-client-banner above, available directly on each "Без клиент" card without first
	// expanding it (no need to load its keywords/tags/rules/examples just to move it).
	movingSetId: string | null = null;
	moveClientQuery = '';
	moveClientResults: ManagementClientResponse[] = [];
	moveClientSearching = false;

	constructor(
		private socialApi: SocialApiService,
		private clientApi: ManagementClientControllerService,
		private categoryApi: ManagementCategoryClippingControllerService,
		private tagApi: ManagementTagClippingControllerService,
		private sharedService: SharedService
	) {}

	ngOnInit(): void {
		this.loadClients();
		this.loadUnassignedCount();
	}

	get filteredClients(): SocialClientSummary[] {
		const q = this.clientSearchText.trim().toLowerCase();
		if (!q) {
			return this.clients;
		}
		return this.clients.filter(c => c.clientName.toLowerCase().includes(q));
	}

	private loadClients(): void {
		this.clientsLoading = true;
		this.socialApi
			.listClientsWithKeywords()
			.pipe(take(1))
			.subscribe({
				next: clients => {
					this.clients = clients || [];
					this.clientsLoading = false;
				},
				error: () => {
					this.clients = [];
					this.clientsLoading = false;
				},
			});
	}

	private loadUnassignedCount(): void {
		this.socialApi
			.listUnassignedSets()
			.pipe(take(1))
			.subscribe({ next: sets => (this.unassignedCount = (sets || []).length), error: () => (this.unassignedCount = 0) });
	}

	// "+ Нов клиент" (section 2): searches the FULL client list (unlike the left list, which only
	// shows clients that already have keywords), so a brand-new client can be picked at all.
	toggleNewClientPicker(): void {
		this.showNewClientPicker = !this.showNewClientPicker;
		this.newClientQuery = '';
		this.newClientResults = [];
	}

	searchNewClient(): void {
		if (!this.newClientQuery.trim()) {
			this.newClientResults = [];
			return;
		}
		this.newClientSearching = true;
		this.clientApi
			.getClients({ name: this.newClientQuery.trim() }, 0, 20)
			.pipe(take(1))
			.subscribe({
				next: page => {
					this.newClientResults = page.content || [];
					this.newClientSearching = false;
				},
				error: () => {
					this.newClientResults = [];
					this.newClientSearching = false;
				},
			});
	}

	pickNewClient(client: ManagementClientResponse): void {
		this.showNewClientPicker = false;
		this.newClientQuery = '';
		this.newClientResults = [];
		this.selectClient(client.id!, client.name || '');
	}

	selectClient(clientId: string, clientName: string): void {
		this.selectedClientId = clientId;
		this.selectedClientName = clientName;
		this.selectedIsUnassigned = false;
		this.closeExpandedSet();
		this.loadSetsForSelectedClient();
		this.loadExcludedProfiles();
	}

	selectUnassigned(): void {
		this.selectedClientId = null;
		this.selectedClientName = '';
		this.selectedIsUnassigned = true;
		this.closeExpandedSet();
		this.loadSets();
		this.excludedProfiles = [];
	}

	private loadSets(): void {
		this.setsLoading = true;
		this.socialApi
			.listUnassignedSets()
			.pipe(take(1))
			.subscribe({
				next: sets => {
					this.sets = sets || [];
					this.setsLoading = false;
				},
				error: () => {
					this.sets = [];
					this.setsLoading = false;
				},
			});
	}

	// A client with zero groups yet (reached through "+ Нов клиент") gets one created right away,
	// invisibly - the manager never sees a "create a set" step, just an empty screen ready to fill in.
	private loadSetsForSelectedClient(): void {
		this.setsLoading = true;
		this.socialApi
			.listSetsForClient(this.selectedClientId!)
			.pipe(take(1))
			.subscribe({
				next: sets => {
					if (sets.length === 0) {
						this.socialApi
							.createSetForClient(this.selectedClientId!, 'Главна група', this.selectedClientName)
							.pipe(take(1))
							.subscribe({
								next: created => {
									this.sets = [this.toSummaryFromSet(created)];
									this.setsLoading = false;
									this.openSoleSetIfNeeded();
								},
								error: () => {
									this.sets = [];
									this.setsLoading = false;
								},
							});
					} else {
						this.sets = sets;
						this.setsLoading = false;
						this.openSoleSetIfNeeded();
					}
				},
				error: () => {
					this.sets = [];
					this.setsLoading = false;
				},
			});
	}

	private toSummaryFromSet(set: SocialSet): SocialSetSummary {
		return {
			id: set.id,
			clientId: set.clientId,
			clientName: set.clientName,
			name: set.name,
			tags: set.tags,
			keywordCount: set.keywords.length,
			exampleCount: set.examples.length,
			hasRules: !!set.rules?.trim(),
		};
	}

	private openSoleSetIfNeeded(): void {
		if (!this.selectedIsUnassigned && this.sets.length === 1) {
			this.openSet(this.sets[0]);
		}
	}

	private loadExcludedProfiles(): void {
		if (!this.selectedClientId) {
			this.excludedProfiles = [];
			return;
		}
		this.socialApi
			.listExcludedProfilesForClient(this.selectedClientId)
			.pipe(take(1))
			.subscribe({ next: profiles => (this.excludedProfiles = profiles || []), error: () => (this.excludedProfiles = []) });
	}

	// Only used in the "Без клиент" bucket (a flat list of orphan групи) - a client's own screen
	// uses startAddGroup/confirmAddGroup below instead.
	createSet(): void {
		if (!this.newSetName.trim()) {
			return;
		}
		this.socialApi
			.createUnassignedSet(this.newSetName.trim())
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.newSetName = '';
					this.sharedService.showNotification.next({ message: 'Групата е создадена.', type: 'SUCCESS' });
					this.loadSets();
					this.loadUnassignedCount();
				},
				error: () => this.sharedService.showNotification.next({ message: 'Не успеа создавањето.', type: 'ERROR' }),
			});
	}

	startAddGroup(): void {
		this.addingGroup = true;
		this.newGroupName = '';
	}

	cancelAddGroup(): void {
		this.addingGroup = false;
		this.newGroupName = '';
	}

	confirmAddGroup(): void {
		const name = this.newGroupName.trim();
		if (!name || !this.selectedClientId) {
			return;
		}
		this.socialApi
			.createSetForClient(this.selectedClientId, name, this.selectedClientName)
			.pipe(take(1))
			.subscribe({
				next: created => {
					const summary = this.toSummaryFromSet(created);
					this.sets = [...this.sets, summary];
					this.addingGroup = false;
					this.newGroupName = '';
					// CLIPPING-610 point 4: the new group used to land collapsed, with no obvious
					// affordance that its header is clickable - open it right away, same as the
					// sole-group case (openSoleSetIfNeeded), so it is immediately editable.
					this.openSet(summary);
				},
				error: () => this.sharedService.showNotification.next({ message: 'Не успеа создавањето.', type: 'ERROR' }),
			});
	}

	// Krug 2 point 1: inline rename, same pattern as startKeywordEdit/saveKeywordEdit.
	startSetEdit(set: SocialSetSummary): void {
		this.editingSetId = set.id;
		this.editingSetName = set.name;
	}

	cancelSetEdit(): void {
		this.editingSetId = null;
		this.editingSetName = '';
	}

	saveSetEdit(set: SocialSetSummary): void {
		const name = this.editingSetName.trim();
		if (!name) {
			return;
		}
		this.socialApi
			.renameSet(set.id, name)
			.pipe(take(1))
			.subscribe({
				next: updated => {
					set.name = updated.name;
					if (this.expandedSet && this.expandedSet.id === set.id) {
						this.expandedSet.name = updated.name;
					}
					this.editingSetId = null;
					this.editingSetName = '';
				},
				error: () => this.sharedService.showNotification.next({ message: 'Не успеа зачувувањето.', type: 'ERROR' }),
			});
	}

	deleteSet(set: SocialSetSummary): void {
		if (!confirm(`Да се избрише групата „${set.name}"?`)) {
			return;
		}
		this.socialApi
			.deleteSet(set.id)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sets = this.sets.filter(s => s.id !== set.id);
					if (this.expandedSetId === set.id) {
						this.closeExpandedSet();
					}
					if (this.selectedIsUnassigned) {
						this.loadUnassignedCount();
					}
				},
				error: () => this.sharedService.showNotification.next({ message: 'Не успеа бришењето.', type: 'ERROR' }),
			});
	}

	// Whole-client delete (section 2) - every group/keyword/rule/example of this client at once.
	// Created clips are never touched (the backend only removes decisions and examples).
	deleteClient(): void {
		if (!this.selectedClientId || this.deletingClient) {
			return;
		}
		if (!confirm(`Сигурно да се избрише клиентот „${this.selectedClientName}" и сите негови зборови/групи? Веќе создадените клипови не се допираат.`)) {
			return;
		}
		this.deletingClient = true;
		this.socialApi
			.deleteAllForClient(this.selectedClientId)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.deletingClient = false;
					this.sharedService.showNotification.next({ message: 'Клиентот е избришан.', type: 'SUCCESS' });
					this.selectedClientId = null;
					this.selectedClientName = '';
					this.sets = [];
					this.closeExpandedSet();
					this.loadClients();
				},
				error: () => {
					this.deletingClient = false;
					this.sharedService.showNotification.next({ message: 'Не успеа бришењето.', type: 'ERROR' });
				},
			});
	}

	openSet(summary: SocialSetSummary): void {
		if (this.expandedSetId === summary.id) {
			return;
		}
		this.expandedSetId = summary.id;
		this.expandedSetLoading = true;
		this.openSections = { keywords: true, tags: true, rules: true, examples: true, excluded: true };
		this.tagsAutoExpandDone = false;

		this.socialApi
			.getSet(summary.id)
			.pipe(take(1))
			.subscribe({
				next: set => {
					this.expandedSet = set;
					this.rulesText = set.rules || '';
					this.selectedTagIds = (set.tags || []).map(t => t.id);
					this.expandedSetLoading = false;
					this.loadKeywordHealth(summary.id);
					// Point 8: "Тагови" starts open by default - auto-expand its relevant
					// categories right away, not only when the admin toggles the section by hand.
					if (this.openSections.tags) {
						this.loadCategoriesOnce();
						this.autoExpandSelectedCategories();
					}
				},
				error: () => {
					this.expandedSetLoading = false;
					this.sharedService.showNotification.next({ message: 'Не можеше да се вчита групата.', type: 'ERROR' });
				},
			});
	}

	// Multi-group ("Без клиент", or a client with 2+ групи) click-to-expand/collapse header.
	toggleSet(summary: SocialSetSummary): void {
		if (this.expandedSetId === summary.id) {
			this.closeExpandedSet();
			return;
		}
		this.openSet(summary);
	}

	private closeExpandedSet(): void {
		this.expandedSetId = null;
		this.expandedSet = null;
		this.keywordHealth = {};
		this.keywordTagPickerKeyword = null;
	}

	private loadKeywordHealth(setId: string): void {
		this.socialApi
			.getKeywordHealth(setId)
			.pipe(take(1))
			.subscribe({
				next: health => {
					this.keywordHealth = {};
					for (const h of health || []) {
						this.keywordHealth[h.keywordId] = h;
					}
				},
				error: () => (this.keywordHealth = {}),
			});
	}

	// Dopolnuvanje 2.5: grey = 0 collected in 7 days, orange = collected a lot but accepted none.
	healthClass(keywordId: string): string {
		const h = this.keywordHealth[keywordId];
		if (!h) {
			return '';
		}
		if (h.collected7d === 0) {
			return 'health-grey';
		}
		if (h.collected7d >= 5 && h.accepted7d === 0) {
			return 'health-orange';
		}
		return '';
	}

	private flashThenCollapse(section: SectionKey): void {
		this.savedFlash[section] = true;
		setTimeout(() => {
			this.savedFlash[section] = false;
			this.openSections[section] = false;
		}, 900);
	}

	toggleSection(section: SectionKey): void {
		this.openSections[section] = !this.openSections[section];
	}

	// "Клучни зборови во едно поле" (Izgled na Dosieja 1): Enter adds the single typed word.
	addKeywordFromInput(): void {
		const text = this.newKeywordText.trim();
		if (!text || !this.expandedSet) {
			return;
		}
		this.addKeywords([text]);
		this.newKeywordText = '';
	}

	// A pasted multi-line list adds everything at once - a plain <input> normally swallows
	// newlines on paste, so this reads the clipboard directly and, when it is more than one
	// line, handles the add itself instead of letting the browser drop it into the field.
	onKeywordPaste(event: ClipboardEvent): void {
		const text = event.clipboardData?.getData('text') || '';
		const lines = text
			.split(/\r?\n/)
			.map(l => l.trim())
			.filter(l => l.length > 0);
		if (lines.length > 1) {
			event.preventDefault();
			this.addKeywords(lines);
		}
	}

	private addKeywords(texts: string[]): void {
		if (!this.expandedSet) {
			return;
		}
		this.socialApi
			.addKeywords(this.expandedSet.id, texts)
			.pipe(take(1))
			.subscribe({
				next: added => {
					const existingIds = new Set(this.expandedSet!.keywords.map(k => k.id));
					for (const k of added) {
						if (!existingIds.has(k.id)) {
							this.expandedSet!.keywords.push(k as SocialKeyword);
							existingIds.add(k.id);
						}
					}
					this.bumpSummaryKeywordCount(this.expandedSet!.keywords.length);
					this.flashThenCollapse('keywords');
					// The client may just have gone from 0 to its first keyword - refresh the
					// left list so it appears there too.
					this.loadClients();
				},
				error: () => this.sharedService.showNotification.next({ message: 'Не успеа додавањето.', type: 'ERROR' }),
			});
	}

	deleteKeyword(keywordId: string, keywordText: string): void {
		if (!this.expandedSet) {
			return;
		}
		// Point 6: every ✕ delete asks first - a keyword typed once is tedious to re-add correctly.
		if (!confirm(`Да се избрише клучниот збор „${keywordText}"?`)) {
			return;
		}
		this.socialApi
			.deleteKeyword(this.expandedSet.id, keywordId)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.expandedSet!.keywords = this.expandedSet!.keywords.filter(k => k.id !== keywordId);
					this.bumpSummaryKeywordCount(this.expandedSet!.keywords.length);
				},
				error: () => this.sharedService.showNotification.next({ message: 'Не успеа бришењето.', type: 'ERROR' }),
			});
	}

	// CLIPPING-610 point 5: edit icon left of the ✕ - renames the keyword's text in place instead
	// of delete+re-add, which would silently drop its "+ друг таг" extra tags.
	startKeywordEdit(keyword: SocialKeyword): void {
		this.editingKeywordId = keyword.id;
		this.editingKeywordText = keyword.text;
	}

	cancelKeywordEdit(): void {
		this.editingKeywordId = null;
		this.editingKeywordText = '';
	}

	saveKeywordEdit(): void {
		if (!this.expandedSet || !this.editingKeywordId) {
			return;
		}
		const text = this.editingKeywordText.trim();
		if (!text) {
			return;
		}
		const keywordId = this.editingKeywordId;
		this.socialApi
			.renameKeyword(this.expandedSet.id, keywordId, text)
			.pipe(take(1))
			.subscribe({
				next: updated => {
					const keyword = this.expandedSet!.keywords.find(k => k.id === keywordId);
					if (keyword) {
						keyword.text = updated.text;
					}
					this.editingKeywordId = null;
					this.editingKeywordText = '';
				},
				error: () => this.sharedService.showNotification.next({ message: 'Не успеа зачувувањето.', type: 'ERROR' }),
			});
	}

	private bumpSummaryKeywordCount(count: number): void {
		const summary = this.sets.find(s => s.id === this.expandedSetId);
		if (summary) {
			summary.keywordCount = count;
		}
	}

	loadCategoriesOnce(): void {
		if (this.categoriesLoaded) {
			return;
		}
		this.categoriesLoaded = true;
		this.categoryApi
			.getCategories()
			.pipe(take(1))
			.subscribe({ next: categories => (this.categories = categories || []), error: () => (this.categories = []) });
	}

	toggleCategory(category: CategoryWithTags): void {
		category.opened = !category.opened;
		if (category.opened && !category.tags) {
			this.loadCategoryTags(category);
		}
	}

	private loadCategoryTags(category: CategoryWithTags): void {
		this.tagApi
			.getTags(category.id!)
			.pipe(take(1))
			.subscribe({ next: tags => (category.tags = tags || []), error: () => (category.tags = []) });
	}

	// CLIPPING-610 point 8: opening "Тагови што се доделуваат" should immediately show WHERE the
	// set's already-assigned tags live, not a wall of collapsed categories the admin has to
	// click through one by one to rediscover what's already selected.
	toggleTagsSection(): void {
		this.toggleSection('tags');
		if (this.openSections.tags) {
			this.loadCategoriesOnce();
			this.autoExpandSelectedCategories();
		}
	}

	private tagsAutoExpandDone = false;

	private autoExpandSelectedCategories(): void {
		if (this.tagsAutoExpandDone || !this.expandedSet) {
			return;
		}
		if (!this.categoriesLoaded) {
			// Categories are still loading - try again once they arrive (loadCategoriesOnce's
			// subscribe only runs once per set, so this one retry is enough).
			setTimeout(() => this.autoExpandSelectedCategories(), 300);
			return;
		}
		this.tagsAutoExpandDone = true;
		const wantedCategoryIds = new Set((this.expandedSet.tags || []).map(t => t.categoryId).filter((id): id is string => !!id));
		for (const category of this.categories) {
			if (category.id && wantedCategoryIds.has(category.id) && !category.opened) {
				category.opened = true;
				this.loadCategoryTags(category);
			}
		}
	}

	isTagChecked(tagId: string): boolean {
		return this.selectedTagIds.includes(tagId);
	}

	onTagCheck(event: MatCheckboxChange, tagId: string): void {
		if (event.checked) {
			if (!this.selectedTagIds.includes(tagId)) {
				this.selectedTagIds.push(tagId);
			}
		} else {
			this.selectedTagIds = this.selectedTagIds.filter(id => id !== tagId);
		}
	}

	saveTags(): void {
		if (!this.expandedSet) {
			return;
		}
		this.socialApi
			.updateSetTags(this.expandedSet.id, this.selectedTagIds)
			.pipe(take(1))
			.subscribe({
				next: set => {
					this.expandedSet!.tags = set.tags;
					const summary = this.sets.find(s => s.id === this.expandedSetId);
					if (summary) {
						summary.tags = set.tags;
					}
					this.flashThenCollapse('tags');
				},
				error: () => this.sharedService.showNotification.next({ message: 'Не успеа зачувувањето.', type: 'ERROR' }),
			});
	}

	// "+ друг таг" po zbor (section 2): a keyword's own extra tags, on top of the group's.
	openKeywordTagPicker(keyword: SocialKeyword): void {
		this.loadCategoriesOnce();
		this.keywordTagPickerKeyword = keyword;
		this.keywordTagSelectedIds = (keyword.tags || []).map(t => t.id);
	}

	closeKeywordTagPicker(): void {
		this.keywordTagPickerKeyword = null;
	}

	isKeywordTagChecked(tagId: string): boolean {
		return this.keywordTagSelectedIds.includes(tagId);
	}

	// CLIPPING-610 point 7: a tag already in "Тагови што се доделуваат" (the set's own tags)
	// would be redundant as a keyword's "+ друг таг" - it already gets applied via the set, so
	// offering it again here just invites a confusing double-pick. Filters, never mutates
	// `category.tags` - that array is shared with the set-tags picker above.
	keywordPickerTags(category: CategoryWithTags): ManagementTagResponse[] {
		if (!category.tags) {
			return [];
		}
		return category.tags.filter(t => !this.selectedTagIds.includes(t.id!));
	}

	onKeywordTagCheck(event: MatCheckboxChange, tagId: string): void {
		if (event.checked) {
			if (!this.keywordTagSelectedIds.includes(tagId)) {
				this.keywordTagSelectedIds.push(tagId);
			}
		} else {
			this.keywordTagSelectedIds = this.keywordTagSelectedIds.filter(id => id !== tagId);
		}
	}

	saveKeywordTags(): void {
		if (!this.expandedSet || !this.keywordTagPickerKeyword) {
			return;
		}
		const keyword = this.keywordTagPickerKeyword;
		this.socialApi
			.updateKeywordTags(this.expandedSet.id, keyword.id, this.keywordTagSelectedIds)
			.pipe(take(1))
			.subscribe({
				next: updated => {
					keyword.tags = updated.tags;
					this.sharedService.showNotification.next({ message: 'Зачувано', type: 'SUCCESS' });
					this.keywordTagPickerKeyword = null;
				},
				error: () => this.sharedService.showNotification.next({ message: 'Не успеа зачувувањето.', type: 'ERROR' }),
			});
	}

	saveRules(): void {
		if (!this.expandedSet) {
			return;
		}
		this.savingRules = true;
		this.socialApi
			.updateSet(this.expandedSet.id, this.expandedSet.name, this.rulesText)
			.pipe(take(1))
			.subscribe({
				next: set => {
					this.expandedSet!.rules = set.rules;
					this.savingRules = false;
					const summary = this.sets.find(s => s.id === this.expandedSetId);
					if (summary) {
						summary.hasRules = !!set.rules?.trim();
					}
					this.flashThenCollapse('rules');
				},
				error: () => {
					this.savingRules = false;
					this.sharedService.showNotification.next({ message: 'Не успеа зачувувањето.', type: 'ERROR' });
				},
			});
	}

	addExample(): void {
		if (!this.expandedSet || !this.newExampleSnippet.trim()) {
			return;
		}
		this.socialApi
			.addExample(this.expandedSet.id, this.newExampleSnippet.trim(), this.newExampleDecision, this.newExampleReason || undefined)
			.pipe(take(1))
			.subscribe({
				next: example => {
					this.expandedSet!.examples = [example, ...this.expandedSet!.examples];
					this.newExampleSnippet = '';
					this.newExampleReason = '';
					const summary = this.sets.find(s => s.id === this.expandedSetId);
					if (summary) {
						summary.exampleCount = this.expandedSet!.examples.length;
					}
					this.flashThenCollapse('examples');
				},
				error: () => this.sharedService.showNotification.next({ message: 'Не успеа додавањето на пример.', type: 'ERROR' }),
			});
	}

	deleteExample(exampleId: string): void {
		if (!this.expandedSet) {
			return;
		}
		if (!confirm('Да се избрише овој пример?')) {
			return;
		}
		this.socialApi
			.deleteExample(this.expandedSet.id, exampleId)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.expandedSet!.examples = this.expandedSet!.examples.filter(e => e.id !== exampleId);
					const summary = this.sets.find(s => s.id === this.expandedSetId);
					if (summary) {
						summary.exampleCount = this.expandedSet!.examples.length;
					}
				},
				error: () => this.sharedService.showNotification.next({ message: 'Не успеа бришењето.', type: 'ERROR' }),
			});
	}

	addExcludedProfile(): void {
		if (!this.selectedClientId || !this.newExcludedLink.trim()) {
			return;
		}
		this.socialApi
			.addExcludedProfileForClient(this.selectedClientId, this.newExcludedLink.trim(), this.newExcludedLabel.trim() || undefined)
			.pipe(take(1))
			.subscribe({
				next: profile => {
					this.excludedProfiles = [...this.excludedProfiles, profile];
					this.newExcludedLink = '';
					this.newExcludedLabel = '';
					this.flashThenCollapse('excluded');
				},
				error: err => this.sharedService.showNotification.next({ message: `Не успеа додавањето. ${err?.error?.message || ''}`, type: 'ERROR' }),
			});
	}

	deleteExcludedProfile(id: string, label: string): void {
		if (!this.selectedClientId) {
			return;
		}
		if (!confirm(`Да се избрише исклучениот профил „${label}"?`)) {
			return;
		}
		this.socialApi
			.deleteExcludedProfileForClient(this.selectedClientId, id)
			.pipe(take(1))
			.subscribe({
				next: () => (this.excludedProfiles = this.excludedProfiles.filter(p => p.id !== id)),
				error: () => this.sharedService.showNotification.next({ message: 'Не успеа бришењето.', type: 'ERROR' }),
			});
	}

	// Point 5: edit icon left of the ✕ - label-only (the link rarely needs to change once added).
	startExcludedEdit(profile: SocialExcludedProfile): void {
		this.editingExcludedId = profile.id;
		this.editingExcludedLabel = profile.label || '';
	}

	cancelExcludedEdit(): void {
		this.editingExcludedId = null;
		this.editingExcludedLabel = '';
	}

	saveExcludedEdit(): void {
		if (!this.selectedClientId || !this.editingExcludedId) {
			return;
		}
		const id = this.editingExcludedId;
		this.socialApi
			.updateExcludedProfileForClient(this.selectedClientId, id, this.editingExcludedLabel.trim())
			.pipe(take(1))
			.subscribe({
				next: updated => {
					this.excludedProfiles = this.excludedProfiles.map(p => (p.id === id ? updated : p));
					this.editingExcludedId = null;
					this.editingExcludedLabel = '';
				},
				error: () => this.sharedService.showNotification.next({ message: 'Не успеа зачувувањето.', type: 'ERROR' }),
			});
	}

	startMoveToClient(set: SocialSetSummary): void {
		this.movingSetId = this.movingSetId === set.id ? null : set.id;
		this.moveClientQuery = '';
		this.moveClientResults = [];
	}

	searchMoveClient(): void {
		if (!this.moveClientQuery.trim()) {
			this.moveClientResults = [];
			return;
		}
		this.moveClientSearching = true;
		this.clientApi
			.getClients({ name: this.moveClientQuery.trim() }, 0, 20)
			.pipe(take(1))
			.subscribe({
				next: page => {
					this.moveClientResults = page.content || [];
					this.moveClientSearching = false;
				},
				error: () => {
					this.moveClientResults = [];
					this.moveClientSearching = false;
				},
			});
	}

	moveSetToClient(set: SocialSetSummary, client: ManagementClientResponse): void {
		this.socialApi
			.assignSetClient(set.id, client.id!, client.name || '')
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sets = this.sets.filter(s => s.id !== set.id);
					this.movingSetId = null;
					if (this.expandedSetId === set.id) {
						this.closeExpandedSet();
					}
					this.loadUnassignedCount();
					this.sharedService.showNotification.next({ message: `Групата е префрлена на ${client.name}.`, type: 'SUCCESS' });
				},
				error: () => this.sharedService.showNotification.next({ message: 'Не успеа префрлувањето.', type: 'ERROR' }),
			});
	}

	// Migration note (docs/SOCIJALNI-FAZA1-KLIENTI.md): a set the migration could not resolve
	// unambiguously shows this inline "избери клиент" picker instead of being silently dropped.
	searchAssignClient(): void {
		if (!this.assignClientQuery.trim()) {
			this.assignClientResults = [];
			return;
		}
		this.assignClientSearching = true;
		this.clientApi
			.getClients({ name: this.assignClientQuery.trim() }, 0, 20)
			.pipe(take(1))
			.subscribe({
				next: page => {
					this.assignClientResults = page.content || [];
					this.assignClientSearching = false;
				},
				error: () => {
					this.assignClientResults = [];
					this.assignClientSearching = false;
				},
			});
	}

	assignClientToExpandedSet(client: ManagementClientResponse): void {
		if (!this.expandedSet) {
			return;
		}
		this.socialApi
			.assignSetClient(this.expandedSet.id, client.id!, client.name || '')
			.pipe(take(1))
			.subscribe({
				next: set => {
					this.expandedSet = set;
					this.assignClientQuery = '';
					this.assignClientResults = [];
					this.sharedService.showNotification.next({ message: `Групата е префрлена на ${client.name}.`, type: 'SUCCESS' });
					if (this.selectedIsUnassigned) {
						this.sets = this.sets.filter(s => s.id !== this.expandedSetId);
						this.loadUnassignedCount();
					}
				},
				error: () => this.sharedService.showNotification.next({ message: 'Не успеа префрлувањето.', type: 'ERROR' }),
			});
	}
}
